export interface PublicTemplate {
  id: string;
  name: string;
  prompt: string;
  category: string;
  tags: string[];
  code?: string;
  deployCommand?: string;
  startFrameName?: string;
  endFrameName?: string;
  refs?: string[]; // reference descriptions/filenames
}

export const publicTemplates: PublicTemplate[] = [
  // --- AGENTS ---
  {
    id: 'agent-1',
    name: 'Autonomous Research Assistant',
    prompt: 'A brilliant researcher agent designed to scour scientific papers, synthesize findings, cross-examine claims, and compile clean markdown reports.',
    category: 'agents',
    tags: ['research', 'ai', 'orchestrator'],
    code: `// Autonomous Research Agent Node Orchestrator
import { GoogleGenAI } from "@google/genai";

const ai = new GoogleGenAI();

export async function runResearchAgent(topic: string) {
  console.log("Initializing Agent Research Cycle for:", topic);
  const response = await ai.models.generateContent({
    model: "gemini-2.5-flash",
    contents: \`Perform deep academic synthesis on: \${topic}. Outline counterclaims, verified consensus, and open directions.\`
  });
  return response.text;
}`,
    deployCommand: 'node agent-orchestrator.js',
    refs: ['agent-flowchart.png', 'prompt-instructions.md']
  },
  {
    id: 'agent-2',
    name: 'Customer Support Triager',
    prompt: 'Automated workflow router classifying customer tickets, assigning priorities, and drafting low-latency initial responses with sentiment calibration.',
    category: 'agents',
    tags: ['customer-success', 'automation', 'nlp'],
    code: `// Ticket Router & Draft Generator
export function triageSentiment(body: string) {
  const urgency = /urgent|broken|refund|chargeback/i.test(body) ? 'high' : 'medium';
  return { ticketPriority: urgency, autoDraft: "Dear Customer, we are reviewing your request immediately..." };
}`,
    deployCommand: 'npm run start:support-agent',
    refs: ['support-triage-map.json']
  },

  // --- CRYPTO ---
  {
    id: 'crypto-1',
    name: 'ERC-20 Custom Token Creator',
    prompt: 'Deployable solidity contract creating an ERC-20 token standard with minting caps, transfer tax exemptions, and structural DAO-controlled ownership.',
    category: 'crypto',
    tags: ['solidity', 'token', 'web3'],
    code: `// SPDX-License-Identifier: MIT
pragma solidity ^0.8.20;

import "@openzeppelin/contracts/token/ERC20/ERC20.sol";
import "@openzeppelin/contracts/access/Ownable.sol";

contract ProtoToken is ERC20, Ownable {
    constructor(string memory name, string memory symbol, uint256 initialSupply) ERC20(name, symbol) Ownable(msg.sender) {
        _mint(msg.sender, initialSupply * 10 ** decimals());
    }
}`,
    deployCommand: 'npx hardhat run scripts/deploy.js --network mainnet',
    refs: ['token-logo-ref.png', 'hardhat.config.js']
  },
  {
    id: 'crypto-2',
    name: 'DeFi Arbitrage Flash Loan Bot',
    prompt: 'Uniswap / SushiSwap cross-exchange price disequilibrium checker triggering high-leverage flash loans via Aave pool contracts.',
    category: 'crypto',
    tags: ['arbitrage', 'defi', 'bot'],
    code: `// Flash loan arbitrage logic snippet
contract FlashArbitrageur {
    function executeOperation(address token, uint256 amount, uint256 fee) external returns (bool) {
        // buy low, sell high cross-dex
        return true;
    }
}`,
    deployCommand: 'npx hardhat node && npm run start-arbitrage',
    refs: ['dex-configs.json']
  },

  // --- CHARACTERS ---
  {
    id: 'char-1',
    name: 'Cyberpunk Investigator Rin',
    prompt: 'Visual archetype template: neon-soaked private detective. Slick black trenchcoat, cybernetic left eye casting crimson ambient lens flare, detailed rain puddles reflecting holographic street ads.',
    category: 'characters',
    tags: ['investigator', 'neon', 'urban'],
    refs: ['rin-face-turnaround.jpg', 'reference-texture-cyber-suit.png']
  },
  {
    id: 'char-2',
    name: 'Retro Astronaut "Major Tom"',
    prompt: '1970s analog space retro-futurism. Vintage heavy off-white space suit, dynamic golden bubble visor reflecting deep space nebulae, warm studio side-lighting.',
    category: 'characters',
    tags: ['astronaut', 'retro', 'cosmic'],
    refs: ['major-tom-sheet.jpg']
  },

  // --- SCENES ---
  {
    id: 'scene-1',
    name: 'Neon Grid Synthwave Horizon',
    prompt: '3D retro grid expanding to infinity beneath a massive low-poly neon wireframe sun. Warm key light reflection, volumetric purple fog, glowing neon teal grid lines.',
    category: 'scenes',
    tags: ['synthwave', '3d', 'loop'],
    refs: ['sun-mesh-wires.png', 'purple-fog-lookup.exr']
  },
  {
    id: 'scene-2',
    name: 'Floating Zen Monolith Temple',
    prompt: 'Massive obsidian cube monolith hovering in mid-air surrounded by cherry blossom petal particles. Sun beams filtering through soft mist, tranquil reflections.',
    category: 'scenes',
    tags: ['zen', 'cinematic', 'minimalist'],
    refs: ['monolith-heightmap.tif', 'blossoms-sprite.png']
  },

  // --- FILM ---
  {
    id: 'film-1',
    name: 'Cinematic Camera Glide "Vertigo"',
    prompt: 'Slow-motion forward tracking glide while zooming outwards gently, creating a volumetric vertigo perspective warp in a gothic sci-fi corridor.',
    category: 'film',
    tags: ['cinematic', 'dolly-zoom', 'scifi'],
    startFrameName: 'corridor-start.png',
    endFrameName: 'corridor-expanded.png',
    refs: ['corridor_assets_batch.zip']
  },
  {
    id: 'film-2',
    name: 'Slow Motion Bioluminescent Explosion',
    prompt: 'Ultra slow-motion fluid dynamics simulation. Crimson and cyan bioluminescent particles erupting from a central core in free-fall space, soft macro shot.',
    category: 'film',
    tags: ['particles', 'macro', 'vfx'],
    startFrameName: 'explosion-spark.png',
    refs: ['macro-focus-planes.png']
  },

  // --- CODE ---
  {
    id: 'code-1',
    name: 'Next-Gen Portfolio Template',
    prompt: 'Client-side SPA layout with futuristic display font settings, dark cosmic responsive styling, micro-interactivity, and automated page-route transitions.',
    category: 'code',
    tags: ['react', 'vite', 'dark-mode'],
    code: `// Multi-layered Canvas Portfolio App
import React from 'react';
export default function Portfolio() {
  return (
    <div className="min-h-screen bg-[#07070a] text-white flex flex-col justify-center items-center p-8">
      <h1 className="text-4xl tracking-tight font-extrabold uppercase">Protospace</h1>
      <p className="text-gray-400 mt-2 font-mono">Creative Digital Director</p>
    </div>
  );
}`,
    deployCommand: 'npm run build && npm run start',
    refs: ['tailwind.config.js', 'package.json']
  },

  // --- ROBOTICS ---
  {
    id: 'robotics-1',
    name: 'Quadruped Walk Cycle Controller',
    prompt: 'Direct control script generating walking sine-wave paths for a 12-degree-of-freedom mechanical robot dog servo arrangement.',
    category: 'robotics',
    tags: ['arduino', 'robot-dog', 'inverse-kinematics'],
    code: `#include <Servo.h>
// Robotic dog gait parameters
const double stride_length = 5.0; // cm
void computeKinematics() {
  // Sine loop generator for stride walking cycle
  double phase = (millis() % 1000) / 1000.0 * 2.0 * M_PI;
  int front_left_angle = 90 + 30 * sin(phase);
  // Apply angles to physical motor shields
}`,
    deployCommand: 'arduino-cli compile --fqbn arduino:avr:uno Quadruped.ino && arduino-cli upload',
    refs: ['quadruped-wiring-diagram.pdf']
  },

  // --- GAMES & WORLDS ---
  {
    id: 'games-1',
    name: 'Infinite Voxels World Engine',
    prompt: 'Three.js procedural world compiler building random noise landscapes, responsive blocks addition, and basic client-side gravitational physics.',
    category: 'games & worlds',
    tags: ['threejs', 'voxels', 'minecraft'],
    code: `// Procedural voxel chunk generator
import * as THREE from 'three';
export function makeChunk(xOffset, zOffset) {
  const geom = new THREE.BoxGeometry(1, 1, 1);
  const mat = new THREE.MeshBasicMaterial({ color: 0x44aa88 });
  const instanced = new THREE.InstancedMesh(geom, mat, 1024);
  // compute perlin noise to set positions
  return instanced;
}`,
    deployCommand: 'npm start -- --host 0.0.0.0 --port 3000',
    refs: ['perlin-noise.js']
  }
];
